import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  FileText,
  Download,
  Search,
  Eye,
  Copy,
  Check,
  X,
} from "lucide-react";
import { apiurl } from "../config/config";
import Layout, { Container } from "../components/layout";

export default function ResumeList() {
  const [files, setFiles] = useState([]);
  const [filteredFiles, setFilteredFiles] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [copied, setCopied] = useState(false);

  const filesPerPage = 100;

  // 🧩 Fetch Files
  useEffect(() => {
    const fetchFiles = async () => {
      try {
        const res = await axios.get(`${apiurl}/api/media/resumes`);
        if (res.data && Array.isArray(res.data.files)) {
          const sorted = res.data.files
            .filter((f) => f && f.fileUrl)
            .sort((a, b) => new Date(b.lastModified) - new Date(a.lastModified));
          setFiles(sorted);
          setFilteredFiles(sorted);
        } else {
          setFiles([]);
          setFilteredFiles([]);
        }
      } catch (err) {
        console.error("Error fetching resumes:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchFiles();
  }, []);

  // 🔍 Search
  useEffect(() => {
    const filtered = files.filter(
      (f) => f?.key?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredFiles(filtered);
    setCurrentPage(1);
  }, [searchTerm, files]);

  // 📄 Pagination
  const indexOfLastFile = currentPage * filesPerPage;
  const indexOfFirstFile = indexOfLastFile - filesPerPage;
  const currentFiles = filteredFiles.slice(indexOfFirstFile, indexOfLastFile);
  const totalPages = Math.ceil(filteredFiles.length / filesPerPage);

  // 🧮 Helpers
  const formatDate = (date) =>
    new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  const formatSize = (size) => {
    if (!size) return "—";
    if (size < 1024) return size + " B";
    if (size < 1048576) return (size / 1024).toFixed(2) + " KB";
    return (size / 1048576).toFixed(2) + " MB";
  };

  const handleCopy = (url) => {
    if (!url) return;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1000);
  };

  return (
    <Layout>
      <Container overflow={"overflow-scroll"}>
        <div className="min-h-screen bg-gray-50 p-4">
          <div className="w-full mx-auto">
            <h1 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
              <FileText className="w-6 h-6 text-blue-600" /> Resume Repository
            </h1>

            {/* 🔍 Search */}
            <div className="relative mb-6">
              <Search className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by filename..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>

            {/* 🌀 Loading or Empty */}
            {loading ? (
              <div className="text-center py-20 text-gray-500">
                Loading resumes...
              </div>
            ) : currentFiles.length === 0 ? (
              <div className="text-center py-20 text-gray-500">
                No resumes found
              </div>
            ) : (
              <>
                {/* 🧾 Table */}
                <div className="overflow-x-auto border border-gray-200 rounded-lg shadow-sm bg-white">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-100 text-gray-700">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium">#</th>
                        <th className="px-4 py-3 text-left font-medium">
                          File Name
                        </th>
                        <th className="px-4 py-3 text-left font-medium">Date</th>
                        <th className="px-4 py-3 text-left font-medium">Size</th>
                        <th className="px-4 py-3 text-center font-medium">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {currentFiles.map((file, idx) => (
                        <tr
                          key={file.key || idx}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-4 py-3 text-gray-500">
                            {indexOfFirstFile + idx + 1}
                          </td>
                          <td className="px-4 py-3 font-medium text-gray-800 truncate max-w-xs">
                            {file.key?.split("/").pop() || "Unnamed"}
                          </td>
                          <td className="px-4 py-3 text-gray-600">
                            {formatDate(file.lastModified)}
                          </td>
                          <td className="px-4 py-3 text-gray-600">
                            {formatSize(file.size)}
                          </td>
                          <td className="px-4 py-3 flex justify-center items-center gap-3">
                            {/* 👁️ View */}
                            {file.fileUrl ? (
                              <button
                                onClick={() => setSelectedFile(file)}
                                className="text-blue-600 hover:text-blue-800"
                                title="View"
                              >
                                <Eye className="w-5 h-5" />
                              </button>
                            ) : (
                              <span className="text-gray-400">N/A</span>
                            )}

                            {/* ⬇️ Download */}
                            {file.fileUrl && (
                              <a
                                href={file.fileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-green-600 hover:text-green-800"
                                title="Download"
                              >
                                <Download className="w-5 h-5" />
                              </a>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* 🧭 Pagination */}
                <div className="flex justify-center items-center mt-8 gap-2 flex-wrap">
                  <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => p - 1)}
                    className="px-4 py-2 border rounded-lg disabled:opacity-50 hover:bg-gray-100"
                  >
                    Prev
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .slice(
                      Math.max(0, currentPage - 3),
                      Math.min(totalPages, currentPage + 2)
                    )
                    .map((pageNum) => (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-4 py-2 border rounded-lg ${
                          pageNum === currentPage
                            ? "bg-blue-600 text-white"
                            : "hover:bg-gray-100"
                        }`}
                      >
                        {pageNum}
                      </button>
                    ))}
                  <button
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((p) => p + 1)}
                    className="px-4 py-2 border rounded-lg disabled:opacity-50 hover:bg-gray-100"
                  >
                    Next
                  </button>
                </div>

                <p className="text-center text-sm text-gray-500 mt-3">
                  Showing {indexOfFirstFile + 1}–
                  {Math.min(indexOfLastFile, filteredFiles.length)} of{" "}
                  {filteredFiles.length} files
                </p>
              </>
            )}
          </div>
        </div>

        {/* 👁️ File Modal */}
        {selectedFile && selectedFile.fileUrl && (
          <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-center">
            <div className="bg-white rounded-xl shadow-xl max-w-7xl w-full p-6 relative">
              <button
                className="absolute top-3 right-3 text-gray-500 hover:text-gray-800"
                onClick={() => setSelectedFile(null)}
              >
                <X className="w-5 h-5" />
              </button>

              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                {selectedFile.key.split("/").pop()}
              </h2>

              {/* File Preview */}
              {(() => {
                const fileUrl = selectedFile.fileUrl;
                if (!fileUrl) return null;
                const ext = fileUrl.split(".").pop()?.toLowerCase();

                if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext))
                  return (
                    <img
                      src={fileUrl}
                      alt="preview"
                      className="w-full h-[70vh] object-contain rounded-lg border"
                    />
                  );
                if (ext === "pdf")
                  return (
                    <iframe
                      src={fileUrl}
                      title="PDF"
                      className="w-full h-[70vh] rounded-lg border"
                    ></iframe>
                  );
                if (
                  ["doc", "docx", "xls", "xlsx", "ppt", "pptx"].includes(ext)
                )
                  return (
                    <iframe
                      src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                        fileUrl
                      )}`}
                      title="Office File"
                      className="w-full h-[70vh] rounded-lg border"
                    ></iframe>
                  );
                return (
                  <p className="text-center text-gray-500 py-10">
                    Preview not available for this file type.
                  </p>
                );
              })()}

              <div className="flex justify-end mt-4 gap-3">
                <button
                  onClick={() => handleCopy(selectedFile.fileUrl)}
                  className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-all ${
                    copied
                      ? "bg-green-100 text-green-700"
                      : "bg-gray-100 hover:bg-gray-200"
                  }`}
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4" /> Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" /> Copy Link
                    </>
                  )}
                </button>
                <a
                  href={selectedFile.fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <Download className="w-4 h-4" /> Download
                </a>
              </div>
            </div>
          </div>
        )}
      </Container>
    </Layout>
  );
}